import { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Users, 
    DollarSign, 
    Calendar, 
    FileText, 
    Settings, 
    BarChart, 
    Search, 
    Plus, 
    Edit, 
    Trash2, 
    Eye, 
    Phone, 
    Mail, 
    MapPin,
    CheckCircle,
    Clock,
    AlertCircle,
    TrendingUp,
    TrendingDown,
    UserCheck,
    FileCheck,
    Calculator,
    Shield,
    LogOut,
    User,
    Check,
    X,
    Globe
} from 'react-feather';
import axios from 'axios';
import AddClientModal from './AddClientModal';
const primetaxLogo = 'https://primetaxonline.com/img/logos/primetaxlogo3.png';

// Controlled SearchInput component
const SearchInput = ({ value, setValue, onSearch, placeholder }) => {
    const timeoutRef = useRef(null);
    const handleChange = (e) => {
        const newValue = e.target.value;
        setValue(newValue);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            onSearch(newValue);
        }, 500);
    };
    useEffect(() => () => clearTimeout(timeoutRef.current), []);
    return (
        <input
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
        />
    );
};

function AdminDashboard() {
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        stats: {},
        recentAppointments: [],
        recentClients: [],
        pendingTasks: []
    });
    const [clients, setClients] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [appointmentPagination, setAppointmentPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 20
    });
    const [appointmentFilters, setAppointmentFilters] = useState({
        search: '',
        status: ''
    });
    const [clientPagination, setClientPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 50
    });
    const [clientFilters, setClientFilters] = useState({
        search: ''
    });
    const [pricing, setPricing] = useState({
        nonResidentTax: 249,
        residentTax: 349,
        expatriateTax: 449,
        adminServices: 199,
        businessTax: 549,
        llcCreation: 1450
    });
    const [searchValue, setSearchValue] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [showEditClientModal, setShowEditClientModal] = useState(false);

    // API proxied through Nginx - uses same domain and SSL cert
    const api_url = window.location.origin;

    useEffect(() => {
        loadDashboardData();
        loadPricing();
        loadClients(1, clientFilters);
        loadAppointments();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${api_url}/api/admin/dashboard`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDashboardData(response.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPricing = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${api_url}/api/admin/pricing`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Loaded pricing from backend:', response.data);
            setPricing(response.data);
        } catch (error) {
            console.error('Error loading pricing:', error);
            // If backend fails, use default pricing
            setPricing({
                nonResidentTax: 249,
                residentTax: 349,
                expatriateTax: 449,
                adminServices: 199,
                businessTax: 549,
                llcCreation: 1450
            });
        }
    };

    const loadClients = async (page = 1, filters = clientFilters) => {
        try {
            const token = localStorage.getItem('adminToken');
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '50',
                search: filters.search
            });
            
            const response = await axios.get(`${api_url}/api/admin/clients?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setClients(response.data.clients);
            setClientPagination(response.data.pagination);
        } catch (error) {
            console.error('Error loading clients:', error);
        }
    };

    const loadAppointments = async (page = 1, filters = appointmentFilters) => {
        try {
            const token = localStorage.getItem('adminToken');
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
                search: filters.search,
                status: filters.status
            });
            
            const response = await axios.get(`${api_url}/api/admin/appointments?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setAppointments(response.data.appointments);
            setAppointmentPagination(response.data.pagination);
        } catch (error) {
            console.error('Error loading appointments:', error);
        }
    };

    const updatePricing = async (service, newPrice) => {
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`${api_url}/api/admin/pricing`, {
                service,
                price: newPrice
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPricing(prev => ({ ...prev, [service]: newPrice }));
        } catch (error) {
            console.error('Error updating pricing:', error);
        }
    };

    const initializePricing = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const defaultPricing = {
                nonResidentTax: 249,
                residentTax: 349,
                expatriateTax: 449,
                adminServices: 199,
                businessTax: 549,
                llcCreation: 1450
            };

            for (const [service, price] of Object.entries(defaultPricing)) {
                await axios.put(`${api_url}/api/admin/pricing`, {
                    service,
                    price: price
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            setPricing(defaultPricing);
            alert('Pricing initialized successfully!');
        } catch (error) {
            console.error('Error initializing pricing:', error);
            alert('Error initializing pricing. Please try again.');
        }
    };

    const handleSaveClient = async (clientData) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.post(`${api_url}/api/admin/clients`, clientData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Reload clients list
            await loadClients();
            
            // Show success message (you can add a toast notification here)
            alert('Client added successfully!');
            
            return response.data;
        } catch (error) {
            console.error('Error saving client:', error);
            alert('Error saving client. Please try again.');
            throw error;
        }
    };

    const updateServicePricing = async (clientId, serviceId, price) => {
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`${api_url}/api/admin/clients/${clientId}/services/${serviceId}`, {
                price: price
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            loadClients(); // Refresh clients to show updated pricing
        } catch (error) {
            console.error('Error updating service pricing:', error);
        }
    };

    const updateAppointmentStatus = async (appointmentId, status) => {
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`${api_url}/api/admin/appointments/${appointmentId}/status`, {
                status: status
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            loadAppointments(); // Refresh appointments to show updated status
        } catch (error) {
            console.error('Error updating appointment status:', error);
        }
    };

    const handleViewAppointment = (appointment) => {
        setSelectedAppointment(appointment);
        setShowAppointmentModal(true);
    };

    const handleEditAppointment = (appointment) => {
        setSelectedAppointment(appointment);
        setShowAppointmentModal(true);
    };

    const handleAppointmentPageChange = (newPage) => {
        loadAppointments(newPage, appointmentFilters);
    };

    const handleClientPageChange = (newPage) => {
        loadClients(newPage, clientFilters);
    };

    const handleAppointmentFilterChange = (filterType, value) => {
        setAppointmentFilters(prev => ({ ...prev, [filterType]: value }));
        // For status changes, load immediately
        if (filterType === 'status') {
            loadAppointments(1, { ...appointmentFilters, [filterType]: value });
        }
    };

    // Handle search from SearchInput component
    const handleAppointmentSearch = useCallback((searchTerm) => {
        setAppointmentFilters(prev => ({ ...prev, search: searchTerm }));
        loadAppointments(1, { ...appointmentFilters, search: searchTerm });
    }, [appointmentFilters, loadAppointments]);

    // Handle client search
    const handleClientSearch = useCallback((searchTerm) => {
        setClientFilters(prev => ({ ...prev, search: searchTerm }));
        loadClients(1, { ...clientFilters, search: searchTerm });
    }, [clientFilters, loadClients]);



    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
    };

    const handleViewClient = async (client) => {
        try {
            const response = await axios.get(`${api_url}/api/admin/clients/${client._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
            });
            setSelectedClient(response.data);
            setShowClientModal(true);
        } catch (error) {
            console.error('Error loading client details:', error);
            alert('Error loading client details');
        }
    };

    const handleEditClient = (client) => {
        console.log('Editing client:', client);
        console.log('Client ID field:', client._id || client.id);
        setEditingClient(client);
        setShowEditClientModal(true);
    };

    const handleUpdateClient = async (clientData) => {
        try {
            // Validate that editingClient exists and has an _id
            if (!editingClient || !editingClient._id) {
                console.error('editingClient or editingClient._id is missing:', editingClient);
                alert('Error: Client ID is missing. Please try again.');
                return;
            }

            console.log('Updating client with ID:', editingClient._id);
            console.log('Client data:', clientData);

            const response = await axios.put(`${api_url}/api/admin/clients/${editingClient._id}`, clientData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
            });
            
            setShowEditClientModal(false);
            setEditingClient(null);
            loadClients(clientPagination.currentPage, clientFilters);
            
            // Show appropriate message based on response
            if (response.data.newClientId) {
                alert('Client converted to dedicated client and updated successfully!');
            } else {
                alert('Client updated successfully');
            }
        } catch (error) {
            console.error('Error updating client:', error);
            alert('Error updating client');
        }
    };

    const handleDeleteClient = async (client) => {
        if (!window.confirm(`Are you sure you want to delete ${client.Name} ${client.LastName}? This action cannot be undone.`)) {
            return;
        }

        try {
            await axios.delete(`${api_url}/api/admin/clients/${client._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
            });
            
            loadClients(clientPagination.currentPage, clientFilters);
            alert('Client deleted successfully');
        } catch (error) {
            console.error('Error deleting client:', error);
            alert('Error deleting client');
        }
    };

    const calculateTotalRevenue = () => {
        if (!clients) return 0;
        return clients.reduce((total, client) => {
            if (Array.isArray(client.Services)) {
                return total + client.Services.reduce((serviceTotal, service) => {
                    return serviceTotal + (service.price || 0);
                }, 0);
            }
            return total;
        }, 0);
    };

    const calculateConfirmedUpcomingAppointments = () => {
        const now = new Date();
        const confirmedUpcoming = appointments.filter(appointment => {
            // Check if status is confirmed
            const isConfirmed = appointment.status === 'Confirmed' || !appointment.status;
            
            // Check if appointment is in the future using AppointmentDate
            const appointmentDate = new Date(appointment.AppointmentDate);
            const isUpcoming = appointmentDate > now;
            
            return isConfirmed && isUpcoming;
        }).length;
        
        return confirmedUpcoming;
    };

    const TabButton = ({ id, icon: Icon, label, count }) => {
        return (
            <button
                onClick={() => setActiveTab(id)}
                className={`btn ${activeTab === id ? 'btn-primary' : 'btn-outline-primary'} me-3`}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <Icon size={18} style={{ marginRight: '8px' }} />
                {label}
                {count && count > 0 && (
                    <span className="badge bg-secondary ms-2">
                        {count}
                    </span>
                )}
            </button>
        );
    };

    const OverviewTab = () => (
        <div>
            {/* Stats Cards */}
            <div className="row g-4 mb-4">
                <div className="col-md-6 col-lg-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="p-3 bg-primary bg-opacity-10 rounded me-3">
                                <Users size={24} style={{ color: '#0d6efd' }} />
                            </div>
                            <div>
                                <p className="text-muted mb-1 small">Total Clients</p>
                                <h4 className="mb-0 fw-bold">{clientPagination.totalCount || 0}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 col-lg-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="p-3 bg-success bg-opacity-10 rounded me-3">
                                <Calendar size={24} style={{ color: '#198754' }} />
                            </div>
                            <div>
                                <p className="text-muted mb-1 small">Appointments</p>
                                <h4 className="mb-0 fw-bold">{dashboardData.stats?.appointments || 0}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 col-lg-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="p-3 bg-warning bg-opacity-10 rounded me-3">
                                <FileText size={24} style={{ color: '#ffc107' }} />
                            </div>
                            <div>
                                <p className="text-muted mb-1 small">Tax Returns</p>
                                <h4 className="mb-0 fw-bold">{dashboardData.stats?.taxReturns || 0}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 col-lg-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <div className="p-3 bg-info bg-opacity-10 rounded me-3">
                                <DollarSign size={24} style={{ color: '#0dcaf0' }} />
                            </div>
                            <div>
                                <p className="text-muted mb-1 small">Total Revenue</p>
                                <h4 className="mb-0 fw-bold">${calculateTotalRevenue().toLocaleString()}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Recent Appointments</h5>
                        </div>
                        <div className="card-body">
                            {dashboardData.recentAppointments?.map((apt, index) => (
                                <div key={index} className="d-flex align-items-center p-3 bg-light rounded mb-2">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px' }}>
                                        <Calendar size={16} style={{ color: '#0d6efd' }} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="mb-1 fw-medium">{apt.Name} {apt.LastName}</p>
                                        <small className="text-muted">
                                            {apt.AppointmentDate ? 
                                                new Date(apt.AppointmentDate).toLocaleDateString() + ' - ' + 
                                                new Date(apt.AppointmentDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                                : 
                                                `${apt.Time} - ${apt.Date}`
                                            }
                                        </small>
                                    </div>
                                    <span className="badge bg-success">Confirmed</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Pending Tasks</h5>
                        </div>
                        <div className="card-body">
                            {clients.filter(client => 
                                Array.isArray(client.Services) && 
                                client.Services.some(service => service.status === 'Pending')
                            ).slice(0, 5).map((client, index) => (
                                <div key={index} className="d-flex align-items-center p-3 bg-warning bg-opacity-10 rounded mb-2">
                                    <Clock size={16} style={{ color: '#ffc107', marginRight: '12px' }} />
                                    <div className="flex-grow-1">
                                        <p className="mb-1 fw-medium">{client.Name} {client.LastName}</p>
                                        <small className="text-muted">
                                            {client.Services.filter(service => service.status === 'Pending')
                                                .map(service => service.name).join(', ')} pending
                                        </small>
                                    </div>
                                </div>
                            ))}
                            {clients.filter(client => 
                                Array.isArray(client.Services) && 
                                client.Services.some(service => service.status === 'Pending')
                            ).length === 0 && (
                                <div className="text-center text-muted py-3">
                                    <Clock size={24} style={{ marginBottom: '8px' }} />
                                    <p className="mb-0">No pending tasks</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const ClientsTab = () => (
        <div>
            {/* Clients Table */}
            <div className="card">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Client</th>
                                    <th>Services</th>
                                    <th>Status</th>
                                    <th>Last Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                    <UserCheck size={20} className="text-secondary" />
                                                </div>
                                                <div>
                                                    <div className="fw-medium">
                                                        {client.Name} {client.LastName}
                                                    </div>
                                                    <small className="text-muted">{client.Email}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {Array.isArray(client.Services) ? (
                                                <div>
                                                    {client.Services.map((service, idx) => (
                                                        <div key={idx} className="mb-2 p-2 border rounded">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div>
                                                                    <span className="badge bg-primary me-1">{service.name}</span>
                                                                    <small className="text-muted">({service.status})</small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="fw-medium me-1">${service.price || 0}</span>
                                                                        <span className="text-muted small">USD</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span>{client.Services || 'N/A'}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge ${
                                                client.Status === 'Active' ? 'bg-success' :
                                                client.Status === 'Pending' ? 'bg-warning' :
                                                'bg-danger'
                                            }`}>
                                                {client.Status}
                                            </span>
                                        </td>
                                        <td>
                                            <small className="text-muted">{client.LastContact}</small>
                                        </td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button 
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleViewClient(client)}
                                                    title="View Details"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    className="btn btn-outline-success"
                                                    onClick={() => handleEditClient(client)}
                                                    title="Edit Client"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button 
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDeleteClient(client)}
                                                    title="Delete Client"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {/* Pagination Controls */}
                {clientPagination.totalPages > 1 && (
                    <div className="card-footer bg-white border-top">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted">
                                Showing {((clientPagination.currentPage - 1) * clientPagination.limit) + 1} to {Math.min(clientPagination.currentPage * clientPagination.limit, clientPagination.totalCount)} of {clientPagination.totalCount} clients
                            </div>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${!clientPagination.hasPrevPage ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handleClientPageChange(clientPagination.currentPage - 1)}
                                            disabled={!clientPagination.hasPrevPage}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    
                                    {/* Page Numbers */}
                                    {Array.from({ length: Math.min(5, clientPagination.totalPages) }, (_, i) => {
                                        const pageNum = i + 1;
                                        return (
                                            <li key={pageNum} className={`page-item ${clientPagination.currentPage === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handleClientPageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    
                                    <li className={`page-item ${!clientPagination.hasNextPage ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handleClientPageChange(clientPagination.currentPage + 1)}
                                            disabled={!clientPagination.hasNextPage}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const AppointmentsTab = ({ searchValue }) => (
        <div>
            {/* Appointments Table */}
            <div className="card">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Client</th>
                                    <th>Date & Time</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {appointments.map((appointment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                    <Calendar size={20} className="text-primary" />
                                                </div>
                                                <div>
                                                    <div className="fw-medium">
                                                        {appointment.Name} {appointment.LastName}
                                                    </div>
                                                    <small className="text-muted">{appointment.Email}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-medium">
                                                    {appointment.AppointmentDate ? new Date(appointment.AppointmentDate).toLocaleDateString() : appointment.Date}
                                                </div>
                                                <small className="text-muted">
                                                    {appointment.AppointmentDate ? new Date(appointment.AppointmentDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : appointment.Time}
                                                </small>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge bg-info">{appointment.Services || 'Consultation'}</span>
                                        </td>
                                        <td>
                                            <select
                                                className={`form-select form-select-sm ${
                                                    appointment.status === 'Confirmed' ? 'border-success' :
                                                    appointment.status === 'Cancelled' ? 'border-danger' :
                                                    'border-warning'
                                                }`}
                                                value={appointment.status || 'Confirmed'}
                                                onChange={(e) => updateAppointmentStatus(appointment._id, e.target.value)}
                                                style={{ width: '120px' }}
                                            >
                                                <option value="Confirmed">Confirmed</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <Phone size={16} className="text-muted me-2" />
                                                <small>{appointment.Phone || 'N/A'}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button 
                                                    className="btn btn-outline-primary" 
                                                    title="View Details"
                                                    onClick={() => handleViewAppointment(appointment)}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    className="btn btn-outline-success" 
                                                    title="Edit"
                                                    onClick={() => handleEditAppointment(appointment)}
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {/* Pagination Controls */}
                {appointmentPagination.totalPages > 1 && (
                    <div className="card-footer bg-white border-top">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted">
                                Showing {((appointmentPagination.currentPage - 1) * appointmentPagination.limit) + 1} to {Math.min(appointmentPagination.currentPage * appointmentPagination.limit, appointmentPagination.totalCount)} of {appointmentPagination.totalCount} appointments
                            </div>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${!appointmentPagination.hasPrevPage ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handleAppointmentPageChange(appointmentPagination.currentPage - 1)}
                                            disabled={!appointmentPagination.hasPrevPage}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    
                                    {/* Page Numbers */}
                                    {Array.from({ length: Math.min(5, appointmentPagination.totalPages) }, (_, i) => {
                                        const pageNum = i + 1;
                                        return (
                                            <li key={pageNum} className={`page-item ${appointmentPagination.currentPage === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handleAppointmentPageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    
                                    <li className={`page-item ${!appointmentPagination.hasNextPage ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handleAppointmentPageChange(appointmentPagination.currentPage + 1)}
                                            disabled={!appointmentPagination.hasNextPage}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const PricingTab = () => {
        const [editingService, setEditingService] = useState(null);
        const [editPrice, setEditPrice] = useState('');

        const handleEditClick = (service, currentPrice) => {
            setEditingService(service);
            setEditPrice(currentPrice.toString());
        };

        const handleSavePrice = async () => {
            if (editPrice && !isNaN(editPrice)) {
                try {
                    await updatePricing(editingService, parseFloat(editPrice));
                    setEditingService(null);
                    setEditPrice('');
                } catch (error) {
                    console.error('Error updating price:', error);
                }
            }
        };

        const handleCancelEdit = () => {
            setEditingService(null);
            setEditPrice('');
        };

        return (
            <div>
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">Service Pricing</h5>
                        <div className="d-flex align-items-center">
                            <small className="text-muted me-3">Prices are automatically updated on the website</small>
                            <button 
                                className="btn btn-outline-primary btn-sm"
                                onClick={initializePricing}
                            >
                                Initialize Pricing
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row g-4">
                            {Object.entries(pricing).map(([service, price]) => (
                                <div key={service} className="col-md-6">
                                    <div className="border rounded p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <h6 className="mb-1 text-capitalize">
                                                    {service.replace(/([A-Z])/g, ' $1').trim()}
                                                </h6>
                                                <small className="text-muted">Current price</small>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                {editingService === service ? (
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm me-2"
                                                            style={{ width: '80px' }}
                                                            value={editPrice}
                                                            onChange={(e) => setEditPrice(e.target.value)}
                                                            placeholder="Price"
                                                        />
                                                        <button 
                                                            className="btn btn-success btn-sm me-1"
                                                            onClick={handleSavePrice}
                                                        >
                                                            <Check size={14} />
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={handleCancelEdit}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center">
                                                        <span className="h5 mb-0 me-3">${price}</span>
                                                        <button 
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => handleEditClick(service, price)}
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const AnalyticsTab = () => (
        <div>
            <div className="row g-4">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Revenue Trends</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">This Month</span>
                                <span className="text-success fw-medium">+12.5%</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Last Month</span>
                                <span className="text-danger fw-medium">-2.1%</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Popular Services</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">LLC Creation</span>
                                <span className="fw-medium">45%</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">Tax Preparation</span>
                                <span className="fw-medium">32%</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Consultation</span>
                                <span className="fw-medium">23%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return <OverviewTab />;
            case 'clients':
                return <ClientsTab />;
            case 'appointments':
                return <AppointmentsTab searchValue={searchValue} />;
            case 'pricing':
                return <PricingTab />;
            case 'analytics':
                return <AnalyticsTab />;
            default:
                return <OverviewTab />;
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
            {/* Header */}
            <div className="bg-white shadow-sm border-bottom">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <div className="d-flex align-items-center">
                            <img 
                                src={primetaxLogo} 
                                alt="PrimeTax Online" 
                                style={{ height: '40px', marginRight: '12px' }}
                            />
                            <div className="d-flex align-items-center">
                                <h1 className="h4 mb-0 me-2">Primetaxonline.com</h1>
                                <span className="badge bg-danger">Admin</span>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                        >
                            <LogOut size={16} style={{ marginRight: '8px' }} />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-bottom">
                <div className="container">
                    <nav className="d-flex py-3">
                        <TabButton id="overview" icon={BarChart} label="Overview" />
                        <TabButton id="clients" icon={Users} label="Clients" count={clientPagination.totalCount} />
                        {(() => {
                            const appointmentCount = calculateConfirmedUpcomingAppointments();
                            return (
                                <TabButton 
                                    id="appointments" 
                                    icon={Calendar} 
                                    label="Appointments" 
                                    count={appointmentCount > 0 ? appointmentCount : null} 
                                />
                            );
                        })()}
                        <TabButton id="pricing" icon={DollarSign} label="Pricing" />
                        <TabButton id="analytics" icon={TrendingUp} label="Analytics" />
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="container py-4">
                {/* Search and Filters - Only show for appointments tab */}
                {activeTab === 'appointments' && (
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <SearchInput
                                value={searchValue}
                                setValue={setSearchValue}
                                onSearch={handleAppointmentSearch}
                                placeholder="Search appointments..."
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={appointmentFilters.status}
                                onChange={(e) => handleAppointmentFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="Confirmed">Confirmed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* Search and Add Client - Only show for clients tab */}
                {activeTab === 'clients' && (
                    <div className="row mb-4">
                        <div className="col-md-8">
                            <SearchInput
                                value={searchValue}
                                setValue={setSearchValue}
                                onSearch={handleClientSearch}
                                placeholder="Search clients..."
                            />
                        </div>
                        <div className="col-md-4">
                            <button 
                                className="btn btn-primary d-flex align-items-center"
                                onClick={() => setShowAddClientModal(true)}
                            >
                                <Plus size={16} style={{ marginRight: '8px' }} />
                                Add Client
                            </button>
                        </div>
                    </div>
                )}
                {renderTabContent()}
            </div>
            
            {/* Add Client Modal */}
            <AddClientModal
                isOpen={showAddClientModal}
                onClose={() => setShowAddClientModal(false)}
                onSave={handleSaveClient}
            />

            {/* Appointment Modal */}
            {showAppointmentModal && selectedAppointment && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                            <div className="modal-header bg-gradient text-white" style={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '15px 15px 0 0',
                                border: 'none'
                            }}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                        <Calendar size={20} />
                                    </div>
                                    <div>
                                        <h5 className="modal-title mb-0 fw-bold">Appointment Details</h5>
                                        <small className="opacity-75">Manage appointment information</small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAppointmentModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                {/* Status Banner */}
                                <div className={`alert ${selectedAppointment.status === 'Confirmed' ? 'alert-success' : 'alert-danger'} d-flex align-items-center mb-4`} style={{ borderRadius: '10px' }}>
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${selectedAppointment.status === 'Confirmed' ? 'bg-success' : 'bg-danger'}`} style={{ width: '30px', height: '30px' }}>
                                        {selectedAppointment.status === 'Confirmed' ? <CheckCircle size={16} className="text-white" /> : <AlertCircle size={16} className="text-white" />}
                                    </div>
                                    <div>
                                        <strong>Status: {selectedAppointment.status || 'Confirmed'}</strong>
                                        <br />
                                        <small className="opacity-75">
                                            {selectedAppointment.status === 'Confirmed' ? 'Appointment is confirmed and ready' : 'Appointment has been cancelled'}
                                        </small>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    {/* Client Information Section */}
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-light border-0">
                                                <h6 className="mb-0 fw-bold text-primary">
                                                    <User size={16} style={{ marginRight: '8px' }} />
                                                    Client Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Full Name</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <User size={16} className="text-primary me-2" />
                                                            <span className="fw-medium">{selectedAppointment.Name} {selectedAppointment.LastName}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Email Address</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Mail size={16} className="text-primary me-2" />
                                                            <span>{selectedAppointment.Email}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Phone Number</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Phone size={16} className="text-primary me-2" />
                                                            <span>{selectedAppointment.Phone || 'Not provided'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Service Requested</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <FileText size={16} className="text-primary me-2" />
                                                            <span className="badge bg-info fs-6">{selectedAppointment.Services || 'Consultation'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Appointment Details Section */}
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-light border-0">
                                                <h6 className="mb-0 fw-bold text-primary">
                                                    <Calendar size={16} style={{ marginRight: '8px' }} />
                                                    Appointment Details
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Date</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Calendar size={16} className="text-primary me-2" />
                                                            <span className="fw-medium">
                                                                {selectedAppointment.AppointmentDate ? new Date(selectedAppointment.AppointmentDate).toLocaleDateString() : selectedAppointment.Date}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Time</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Clock size={16} className="text-primary me-2" />
                                                            <span className="fw-medium">
                                                                {selectedAppointment.AppointmentDate ? new Date(selectedAppointment.AppointmentDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : selectedAppointment.Time}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Status</label>
                                                        <select
                                                            className={`form-select ${selectedAppointment.status === 'Confirmed' ? 'border-success' : 'border-danger'}`}
                                                            value={selectedAppointment.status || 'Confirmed'}
                                                            onChange={(e) => {
                                                                updateAppointmentStatus(selectedAppointment._id, e.target.value);
                                                                setSelectedAppointment({
                                                                    ...selectedAppointment,
                                                                    status: e.target.value
                                                                });
                                                            }}
                                                            style={{ borderRadius: '8px' }}
                                                        >
                                                            <option value="Confirmed">✅ Confirmed</option>
                                                            <option value="Cancelled">❌ Cancelled</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Created</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Clock size={16} className="text-primary me-2" />
                                                            <span>{selectedAppointment.createdAt ? new Date(selectedAppointment.createdAt).toLocaleString() : 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes Section */}
                                    {selectedAppointment.Notes && (
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <FileText size={16} style={{ marginRight: '8px' }} />
                                                        Additional Notes
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="p-3 bg-light rounded">
                                                        <p className="mb-0">{selectedAppointment.Notes}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer border-0 bg-light" style={{ borderRadius: '0 0 15px 15px' }}>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowAppointmentModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        // Add any additional save functionality here
                                        setShowAppointmentModal(false);
                                    }}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client View Modal */}
            {showClientModal && selectedClient && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content border-0 shadow" style={{ borderRadius: '15px' }}>
                            <div className="modal-header bg-primary text-white border-0" style={{ borderRadius: '15px 15px 0 0' }}>
                                <h5 className="modal-title fw-bold">
                                    <UserCheck size={20} style={{ marginRight: '10px' }} />
                                    Client Details
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowClientModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row g-4">
                                    {/* Basic Information */}
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-light border-0">
                                                <h6 className="mb-0 fw-bold text-primary">
                                                    <User size={16} style={{ marginRight: '8px' }} />
                                                    Basic Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Name</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <User size={16} className="text-primary me-2" />
                                                            <span className="fw-medium">{selectedClient.Name} {selectedClient.LastName}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Email</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Mail size={16} className="text-primary me-2" />
                                                            <span>{selectedClient.Email}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Phone</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Phone size={16} className="text-primary me-2" />
                                                            <span>{selectedClient.Phone || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Address</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <MapPin size={16} className="text-primary me-2" />
                                                            <span>{selectedClient.Address || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Source</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <span className="badge bg-info">{selectedClient.Source || 'Website'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium text-muted">Created</label>
                                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                                            <Clock size={16} className="text-primary me-2" />
                                                            <span>{selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleString() : 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Services */}
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-light border-0">
                                                <h6 className="mb-0 fw-bold text-primary">
                                                    <FileText size={16} style={{ marginRight: '8px' }} />
                                                    Services
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                {Array.isArray(selectedClient.Services) && selectedClient.Services.length > 0 ? (
                                                    <div className="row g-3">
                                                        {selectedClient.Services.map((service, index) => (
                                                            <div key={index} className="col-12">
                                                                <div className="border rounded p-3">
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div>
                                                                            <span className="badge bg-primary me-2">{service.name}</span>
                                                                            <span className={`badge ${
                                                                                service.status === 'Completed' ? 'bg-success' :
                                                                                service.status === 'Pending' ? 'bg-warning' :
                                                                                'bg-info'
                                                                            }`}>
                                                                                {service.status}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-end">
                                                                            <div className="fw-medium">${service.price || 0}</div>
                                                                            <small className="text-muted">USD</small>
                                                                        </div>
                                                                    </div>
                                                                    {service.addedDate && (
                                                                        <div className="mt-2">
                                                                            <small className="text-muted">
                                                                                Added: {new Date(service.addedDate).toLocaleDateString()}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                    {service.completedDate && (
                                                                        <div className="mt-1">
                                                                            <small className="text-success">
                                                                                Completed: {new Date(service.completedDate).toLocaleDateString()}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center text-muted py-3">
                                                        <FileText size={24} style={{ marginBottom: '8px' }} />
                                                        <p className="mb-0">No services added</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    {selectedClient.Notes && selectedClient.Notes.length > 0 && (
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <FileText size={16} style={{ marginRight: '8px' }} />
                                                        Notes
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    {selectedClient.Notes.map((note, index) => (
                                                        <div key={index} className="p-3 bg-light rounded mb-2">
                                                            <p className="mb-1">{note.content}</p>
                                                            <small className="text-muted">
                                                                {note.date ? new Date(note.date).toLocaleString() : 'N/A'}
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer border-0 bg-light" style={{ borderRadius: '0 0 15px 15px' }}>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowClientModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        handleEditClient(selectedClient);
                                        setShowClientModal(false);
                                    }}
                                >
                                    Edit Client
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Edit Modal */}
            {showEditClientModal && editingClient && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header bg-gradient text-white border-0" style={{ 
                                borderRadius: '20px 20px 0 0',
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)'
                            }}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                        <Edit size={20} />
                                    </div>
                                    <div>
                                        <h5 className="modal-title fw-bold mb-0">Edit Client</h5>
                                        <small className="opacity-75">Update client information</small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditClientModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    handleUpdateClient({
                                        Name: formData.get('name'),
                                        LastName: formData.get('lastName'),
                                        Email: formData.get('email'),
                                        Phone: formData.get('phone'),
                                        Address: formData.get('address'),
                                        Source: formData.get('source'),
                                        Services: editingClient.Services || []
                                    });
                                }}>
                                    <div className="row g-4">
                                        {/* Personal Information Section */}
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <User size={16} style={{ marginRight: '8px' }} />
                                                        Personal Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-medium text-muted">
                                                                <User size={14} style={{ marginRight: '6px' }} />
                                                                First Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-lg"
                                                                name="name"
                                                                defaultValue={editingClient.Name}
                                                                required
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-medium text-muted">
                                                                <User size={14} style={{ marginRight: '6px' }} />
                                                                Last Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-lg"
                                                                name="lastName"
                                                                defaultValue={editingClient.LastName}
                                                                required
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Information Section */}
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <Phone size={16} style={{ marginRight: '8px' }} />
                                                        Contact Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-medium text-muted">
                                                                <Mail size={14} style={{ marginRight: '6px' }} />
                                                                Email Address
                                                            </label>
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-lg"
                                                                name="email"
                                                                defaultValue={editingClient.Email}
                                                                required
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-medium text-muted">
                                                                <Phone size={14} style={{ marginRight: '6px' }} />
                                                                Phone Number
                                                            </label>
                                                            <input
                                                                type="tel"
                                                                className="form-control form-control-lg"
                                                                name="phone"
                                                                defaultValue={editingClient.Phone}
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Address Section */}
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <MapPin size={16} style={{ marginRight: '8px' }} />
                                                        Address Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-12">
                                                            <label className="form-label fw-medium text-muted">
                                                                <MapPin size={14} style={{ marginRight: '6px' }} />
                                                                Full Address
                                                            </label>
                                                            <textarea
                                                                className="form-control form-control-lg"
                                                                name="address"
                                                                rows="3"
                                                                defaultValue={editingClient.Address}
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                                placeholder="Enter client's full address..."
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Services Section */}
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <FileText size={16} style={{ marginRight: '8px' }} />
                                                        Services & Status
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-12">
                                                            <label className="form-label fw-medium text-muted">
                                                                <Plus size={14} style={{ marginRight: '6px' }} />
                                                                Add New Service
                                                            </label>
                                                            <div className="d-flex gap-2">
                                                                <select
                                                                    className="form-select form-select-lg flex-grow-1"
                                                                    style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                                    onChange={(e) => {
                                                                        if (e.target.value) {
                                                                            const newService = {
                                                                                id: Date.now(),
                                                                                name: e.target.value,
                                                                                status: 'Pending',
                                                                                price: 0,
                                                                                addedDate: new Date().toISOString(),
                                                                                completedDate: null
                                                                            };
                                                                            setEditingClient(prev => ({
                                                                                ...prev,
                                                                                Services: [...(prev.Services || []), newService]
                                                                            }));
                                                                            e.target.value = '';
                                                                        }
                                                                    }}
                                                                >
                                                                                                                                    <option value="">Select a service to add...</option>
                                                                <option value="LLC Creation">LLC Creation</option>
                                                                <option value="Tax Preparation">Tax Preparation</option>
                                                                <option value="Business Formation">Business Formation</option>
                                                                <option value="Consultation">Consultation</option>
                                                                <option value="Investment Advice">Investment Advice</option>
                                                                <option value="Bookkeeping">Bookkeeping</option>
                                                                <option value="Tax Planning">Tax Planning</option>
                                                                <option value="Audit Support">Audit Support</option>
                                                                <option value="Individual Tax Return">Individual Tax Return (Form 1040)</option>
                                                                <option value="Business Tax Return">Business Tax Return</option>
                                                                <option value="EIN Application">EIN Application</option>
                                                                <option value="Operating Agreement">Operating Agreement</option>
                                                                <option value="Bank Account Setup">Bank Account Setup</option>
                                                                <option value="Compliance Guidance">Compliance Guidance</option>
                                                                <option value="Real Estate Investment">Real Estate Investment</option>
                                                                <option value="Stock Market Investment">Stock Market Investment</option>
                                                                <option value="Cryptocurrency Investment">Cryptocurrency Investment</option>
                                                                <option value="Retirement Planning">Retirement Planning</option>
                                                                <option value="Portfolio Management">Portfolio Management</option>
                                                                <option value="Risk Assessment">Risk Assessment</option>
                                                                <option value="Web Design">Web Design</option>
                                                                <option value="Marketing Services">Marketing Services</option>
                                                                <option value="Rental Property Tax">Rental Property Tax</option>
                                                                <option value="Self-Employment Tax">Self-Employment Tax</option>
                                                                <option value="W2 Processing">W2 Processing</option>
                                                                <option value="Interest & Dividend Tax">Interest & Dividend Tax</option>
                                                                <option value="LLC Renewal">LLC Renewal</option>
                                                                <option value="US Bank Account Opening">US Bank Account Opening</option>
                                                                <option value="Non-Resident Tax Return">Non-Resident Tax Return (Form 1040NR)</option>
                                                                <option value="Work & Travel Tax">Work & Travel Tax</option>
                                                                <option value="Tax Amendment">Tax Amendment (Form 1040X)</option>
                                                                <option value="Stimulus Check Recovery">Stimulus Check Recovery</option>
                                                                <option value="Stock Trading Tax">Stock Trading Tax</option>
                                                                <option value="Expatriate Tax Optimization">Expatriate Tax Optimization</option>
                                                                <option value="Foreign Account Reporting">Foreign Account Reporting (FBAR)</option>
                                                                <option value="Corporate Tax Return">Corporate Tax Return (Form 1120)</option>
                                                                <option value="Partnership Tax Return">Partnership Tax Return (Form 1065)</option>
                                                                <option value="S-Corporation Tax Return">S-Corporation Tax Return (Form 1120-S)</option>
                                                                <option value="Business Structure Optimization">Business Structure Optimization</option>
                                                                <option value="Foreign Ownership Reporting">Foreign Ownership Reporting (Form 5472)</option>
                                                                <option value="Foreign Corporation Reporting">Foreign Corporation Reporting</option>
                                                                <option value="K-1 Form Processing">K-1 Form Processing</option>
                                                                <option value="Brand & Logo Registration">Brand & Logo Registration</option>
                                                                <option value="Domain Registration">Domain Registration</option>
                                                                <option value="Web Application Development">Web Application Development</option>
                                                                <option value="Database Management">Database Management</option>
                                                                <option value="Technical Support">Technical Support</option>
                                                                <option value="E-commerce Development">E-commerce Development</option>
                                                                <option value="Cybersecurity">Cybersecurity</option>
                                                                <option value="Online Marketing">Online Marketing</option>
                                                                <option value="Email Newsletter">Email Newsletter</option>
                                                                <option value="IT Team Formation">IT Team Formation</option>
                                                                <option value="Small Capital Investment">Small Capital Investment</option>
                                                                <option value="Large Capital Investment">Large Capital Investment</option>
                                                                <option value="Investment Visa Advisory">Investment Visa Advisory</option>
                                                                <option value="Short-term Investment Plans">Short-term Investment Plans</option>
                                                                <option value="Medium-term Investment Plans">Medium-term Investment Plans</option>
                                                                <option value="Long-term Investment Plans">Long-term Investment Plans</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Display Existing Services */}
                                                        {Array.isArray(editingClient.Services) && editingClient.Services.length > 0 && (
                                                            <div className="col-12">
                                                                <div className="space-y-3">
                                                                    {editingClient.Services.map((service, index) => (
                                                                        <div key={service.id} className="border rounded p-3 bg-light">
                                                                            <div className="row g-3 align-items-center">
                                                                                <div className="col-md-4">
                                                                                    <label className="form-label fw-medium text-muted small">Service Name</label>
                                                                                    <div className="fw-medium">{service.name}</div>
                                                                                </div>
                                                                                <div className="col-md-2">
                                                                                    <label className="form-label fw-medium text-muted small">Status</label>
                                                                                    <select
                                                                                        className="form-select form-select-sm"
                                                                                        value={service.status}
                                                                                        onChange={(e) => {
                                                                                            const updatedServices = [...editingClient.Services];
                                                                                            updatedServices[index].status = e.target.value;
                                                                                            if (e.target.value === 'Completed') {
                                                                                                updatedServices[index].completedDate = new Date().toISOString();
                                                                                            }
                                                                                            setEditingClient(prev => ({
                                                                                                ...prev,
                                                                                                Services: updatedServices
                                                                                            }));
                                                                                        }}
                                                                                    >
                                                                                        <option value="Pending">⏳ Pending</option>
                                                                                        <option value="In Progress">🔄 In Progress</option>
                                                                                        <option value="Completed">✅ Completed</option>
                                                                                        <option value="Cancelled">❌ Cancelled</option>
                                                                                    </select>
                                                                                </div>
                                                                                <div className="col-md-2">
                                                                                    <label className="form-label fw-medium text-muted small">Price (USD)</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        className="form-control form-control-sm"
                                                                                        value={service.price || ''}
                                                                                        onChange={(e) => {
                                                                                            const updatedServices = [...editingClient.Services];
                                                                                            updatedServices[index].price = parseFloat(e.target.value) || 0;
                                                                                            setEditingClient(prev => ({
                                                                                                ...prev,
                                                                                                Services: updatedServices
                                                                                            }));
                                                                                        }}
                                                                                        placeholder="0"
                                                                                    />
                                                                                </div>
                                                                                <div className="col-md-2">
                                                                                    <label className="form-label fw-medium text-muted small">Added</label>
                                                                                    <small className="text-muted d-block">
                                                                                        {service.addedDate ? new Date(service.addedDate).toLocaleDateString() : 'N/A'}
                                                                                    </small>
                                                                                </div>
                                                                                <div className="col-md-2">
                                                                                    <label className="form-label fw-medium text-muted small">Actions</label>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        onClick={() => {
                                                                                            const updatedServices = editingClient.Services.filter((_, i) => i !== index);
                                                                                            setEditingClient(prev => ({
                                                                                                ...prev,
                                                                                                Services: updatedServices
                                                                                            }));
                                                                                        }}
                                                                                    >
                                                                                        <Trash2 size={14} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                            {service.completedDate && (
                                                                                <div className="mt-2">
                                                                                    <small className="text-success">
                                                                                        ✅ Completed: {new Date(service.completedDate).toLocaleDateString()}
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Source Information Section */}
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-light border-0">
                                                    <h6 className="mb-0 fw-bold text-primary">
                                                        <Globe size={16} style={{ marginRight: '8px' }} />
                                                        Source Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-medium text-muted">
                                                                <Globe size={14} style={{ marginRight: '6px' }} />
                                                                Lead Source
                                                            </label>
                                                            <select 
                                                                className="form-select form-select-lg" 
                                                                name="source" 
                                                                defaultValue={editingClient.Source}
                                                                style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                                            >
                                                                <option value="Website">🌐 Website</option>
                                                                <option value="Referral">👥 Referral</option>
                                                                <option value="Social Media">📱 Social Media</option>
                                                                <option value="Advertisement">📢 Advertisement</option>
                                                                <option value="Other">📋 Other</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="modal-footer border-0 bg-light mt-4" style={{ borderRadius: '0 0 20px 20px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-lg"
                                            onClick={() => setShowEditClientModal(false)}
                                            style={{ borderRadius: '10px', padding: '12px 24px' }}
                                        >
                                            <X size={16} style={{ marginRight: '8px' }} />
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg"
                                            style={{ 
                                                borderRadius: '10px', 
                                                padding: '12px 24px',
                                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                                border: 'none'
                                            }}
                                        >
                                            <Check size={16} style={{ marginRight: '8px' }} />
                                            Update Client
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard; 